import { useState, useEffect, useCallback, useRef } from 'react';

// Helper function to get API base URL for embedded widgets
const getApiBaseUrl = () => {
  const scripts = document.getElementsByTagName('script');
  for (let i = 0; i < scripts.length; i++) {
    if (scripts[i].src && scripts[i].src.includes('smushchat-widget.js')) {
      if (scripts[i].src.includes('localhost') || scripts[i].src.includes('127.0.0.1')) {
        return 'http://localhost:3000';
      } else if (scripts[i].src.includes('staging.smushlabs.ai')) {
        return 'https://staging.smushlabs.ai';
      } else if (scripts[i].src.includes('smushlabs.ai')) {
        return 'https://smushlabs.ai';
      } else if (scripts[i].src.includes('workers.dev')) {
        const url = new URL(scripts[i].src);
        return url.origin;
      }
    }
  }
  return window.location.origin; // Fallback for platform mode
};

export const useFormLogic = (chatbotId, isChatOpen, messages, setMessages, saveMessagesToDB, threadId, setThreadId, setIsSending, hasCustomAssistant, hasCustomApiKey, sanitizeResponse, responseDelay, inputRef, isPlatform, threadKey, forceShowFirstForm = false, previewForms = []) => {
  // Forms state
  const [forms, setForms] = useState(previewForms.length > 0 ? previewForms : []);

  // Update forms when previewForms change (for live preview)
  useEffect(() => {
    if (previewForms.length > 0) {
      setForms(previewForms);
    }
  }, [previewForms]);
  const [aiResponseCount, setAiResponseCount] = useState(0);
  const [shownFormSteps, setShownFormSteps] = useState(new Set());
  const [completedFormSteps, setCompletedFormSteps] = useState(new Set());
  const [activeRequiredForm, setActiveRequiredForm] = useState(null);
  const [step0FormCompleted, setStep0FormCompleted] = useState(false);
  const [messagesInitialized, setMessagesInitialized] = useState(false);

  // Track when messages have been initialized (loaded from localStorage or set to initial values)
  useEffect(() => {
    if (messages.length > 0 && !messagesInitialized) {
      // Initialize shownFormSteps from existing messages to prevent duplication on page refresh
      const existingFormSteps = new Set();
      messages.forEach(msg => {
        if (msg.role === 'form' && msg.formData?.step !== undefined) {
          existingFormSteps.add(msg.formData.step);
        }
      });

      if (existingFormSteps.size > 0) {
        setShownFormSteps(existingFormSteps);

        // Check if the last message is a form that should block input
        const lastMessage = messages[messages.length - 1];
        if (lastMessage?.role === 'form' && lastMessage.formData) {
          const hasRequiredFields = lastMessage.formData.fields?.some(field => field.required);
          if (hasRequiredFields) {
            // Check if this form has been submitted by looking for a user message after it
            const formIndex = messages.length - 1;
            const hasUserMessageAfterForm = messages.slice(formIndex + 1).some(msg => msg.role === 'user');

            if (!hasUserMessageAfterForm) {
              setActiveRequiredForm(lastMessage.formData);
            }
          }
        }

        // Check if step 0 form has been completed (for menu display logic)
        if (existingFormSteps.has(0)) {
          // If step 0 form exists, check if it's been submitted
          const step0FormIndex = messages.findIndex(msg => msg.role === 'form' && msg.formData?.step === 0);
          if (step0FormIndex !== -1) {
            const hasUserMessageAfterStep0 = messages.slice(step0FormIndex + 1).some(msg => msg.role === 'user');
            if (hasUserMessageAfterStep0) {
              setStep0FormCompleted(true);
            }
          }
        }
      }

      // Add a small delay to ensure messages are fully stabilized
      const timer = setTimeout(() => {
        setMessagesInitialized(true);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [messages, messagesInitialized, threadId, chatbotId]);

  // Load forms from backend
  const loadForms = useCallback(async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/forms?chatbotId=${chatbotId}`);
      if (response.ok) {
        const data = await response.json();
        const enabledForms = (data.forms || []).filter(form => form.enabled);
        setForms(enabledForms);
      }
    } catch (error) {
      // Silently fail - don't pollute client console
    }
  }, [chatbotId]);

  // Handle step 0 forms when forms are loaded
  useEffect(() => {
    // Only run after messages have been initialized from localStorage to avoid interfering with chat history
    if (messagesInitialized && forms.length > 0 && messages.length > 0) {
      const step0Form = forms.find(form => form.step === 0);
      if (step0Form && !shownFormSteps.has(0)) {
        // Check if form is already present
        const hasFormAlready = messages.some(msg => msg.role === 'form' && msg.formData?.step === 0);

        if (!hasFormAlready) {
          // Add step 0 form after intro message (if any)
          const formMessage = {
            role: 'form',
            content: step0Form.prompt,
            formData: step0Form,
            timestamp: new Date().toISOString()
          };

          setMessages(prev => [...prev, formMessage]);
          setShownFormSteps(prev => new Set([...prev, 0]));

          // If form has required fields, block input
          const hasRequiredFields = step0Form.fields.some(field => field.required);
          if (hasRequiredFields) {
            setActiveRequiredForm(step0Form);
          }
        } else {
          // Form already exists, just update tracking state
          setShownFormSteps(prev => new Set([...prev, 0]));

          // Check if we need to reactivate required form blocking
          const hasRequiredFields = step0Form.fields.some(field => field.required);
          if (hasRequiredFields) {
            // Check if this form is the last message and hasn't been submitted
            const lastMessage = messages[messages.length - 1];
            if (lastMessage?.role === 'form' && lastMessage.formData?.step === 0) {
              setActiveRequiredForm(step0Form);
            }
          }
        }
      }
    }
  }, [messagesInitialized, forms, messages, shownFormSteps, setMessages]);

  // Check if any forms should be displayed at current step
  const checkForFormsToDisplay = useCallback(() => {
    if (forms.length === 0) return;

    // Check if any message is currently streaming - don't show forms during streaming
    const hasStreamingMessage = messages.some(msg => msg.isStreaming);
    if (hasStreamingMessage) {
      return;
    }

    // Adjust step logic to account for intro message:
    // Step 0: Show when aiResponseCount === 1 (only intro message)
    // Step 1: Show when aiResponseCount === 2 (intro + first real AI response)
    // Step N: Show when aiResponseCount === N + 1
    const formToShow = forms.find(form => {
      const expectedAiResponseCount = form.step + 1;
      return expectedAiResponseCount === aiResponseCount &&
        !shownFormSteps.has(form.step) &&
        !completedFormSteps.has(form.step); // Don't show completed forms
    });

    if (formToShow) {
      // Check if form already exists in messages (important for page refresh scenarios)
      const formAlreadyInMessages = messages.some(msg =>
        msg.role === 'form' && msg.formData?.step === formToShow.step
      );

      if (!formAlreadyInMessages) {
        // Add form as a special message type
        const formMessage = {
          role: 'form',
          content: formToShow.prompt,
          formData: formToShow,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, formMessage]);
        setShownFormSteps(prev => new Set([...prev, formToShow.step]));

        // If form has required fields, block input
        const hasRequiredFields = formToShow.fields.some(field => field.required);
        if (hasRequiredFields) {
          setActiveRequiredForm(formToShow);
        }
      } else {
        // Form already exists in messages, just update tracking state
        setShownFormSteps(prev => new Set([...prev, formToShow.step]));

        // Check if we need to reactivate required form blocking
        const hasRequiredFields = formToShow.fields.some(field => field.required);
        if (hasRequiredFields) {
          // Check if this form is the last message and hasn't been submitted
          const lastMessage = messages[messages.length - 1];
          if (lastMessage?.role === 'form' && lastMessage.formData?.step === formToShow.step) {
            setActiveRequiredForm(formToShow);
          }
        }
      }
    }
  }, [forms, aiResponseCount, shownFormSteps, completedFormSteps, setMessages, messages]);

  // Load forms when chatbot is opened
  useEffect(() => {
    if (chatbotId && isChatOpen) {
      loadForms();
    }
  }, [chatbotId, isChatOpen, loadForms]);

  // Force show first form if enabled (for preview)
  useEffect(() => {
    if (forceShowFirstForm && forms.length > 0) {
      // Find the first form
      const firstForm = forms[0];
      if (firstForm) {
        setActiveRequiredForm(firstForm);

        // Ensure the form message is displayed in the chat
        setMessages(prev => {
          // Check if form is already in messages
          const formExists = prev.some(msg => msg.role === 'form' && msg.formData?.id === firstForm.id);

          if (!formExists) {
            const formMessage = {
              role: 'form',
              content: firstForm.prompt,
              formData: firstForm,
              timestamp: new Date().toISOString()
            };
            return [...prev, formMessage];
          }
          return prev;
        });

        // Mark as shown so it doesn't try to show again later via normal logic
        setShownFormSteps(prev => new Set([...prev, firstForm.step]));
      }
    } else if (!forceShowFirstForm) {
      // If forceShowFirstForm is disabled (e.g. switching tabs), clear the forced state
      // But only if it was forced. If it's a real conversation state, we should probably keep it?
      // For preview purposes, we want to clear it so Input shows up in other tabs.
      setActiveRequiredForm(null);
    }
  }, [forceShowFirstForm, forms, setMessages]);

  // Check for forms to display based on AI response count
  useEffect(() => {
    // Only check for forms after messages have been initialized from localStorage
    if (messagesInitialized) {
      // Use requestAnimationFrame to ensure DOM is updated before checking forms
      const animationFrame = requestAnimationFrame(() => {
        // Add a longer delay to ensure streaming responses are fully complete
        // Check if any message is currently streaming
        const hasStreamingMessage = messages.some(msg => msg.isStreaming);
        const delay = hasStreamingMessage ? 1000 : 300; // Longer delay if streaming detected

        setTimeout(() => {
          checkForFormsToDisplay();
        }, delay);
      });

      return () => {
        cancelAnimationFrame(animationFrame);
      };
    }
  }, [checkForFormsToDisplay, messagesInitialized, messages]);

  // Initialize AI response count based on existing messages
  useEffect(() => {
    // Only calculate AI response count after messages have been initialized from localStorage
    if (messagesInitialized) {
      // Only count assistant messages that are not currently streaming and have actual content
      const aiMsgCount = messages.filter(msg =>
        msg.role === 'assistant' &&
        !msg.isStreaming &&
        msg.content &&
        msg.content.trim() !== ''
      ).length;
      setAiResponseCount(aiMsgCount);
    }
  }, [messages, messagesInitialized]);

  // Reset forms state when chatbot ID changes
  const prevChatbotIdRef = useRef();
  useEffect(() => {
    // Only reset if we have a previous chatbot ID and it's different from current
    // This prevents reset on initial page load
    if (prevChatbotIdRef.current && chatbotId && prevChatbotIdRef.current !== chatbotId) {
      // Clear all forms-related state
      setForms([]);
      setShownFormSteps(new Set());
      setCompletedFormSteps(new Set());
      setActiveRequiredForm(null);
      setStep0FormCompleted(false);
      setMessagesInitialized(false); // Reset initialization state for new chatbot
      // Don't reset aiResponseCount as it should be calculated from existing messages
    }

    // Update the ref after the comparison
    prevChatbotIdRef.current = chatbotId;
  }, [chatbotId]);

  // Helper function to store form submission
  const storeFormSubmission = async (formData, formStep, currentForm) => {
    try {
      // Get user location info if available (with timeout)
      let locationInfo = { country: null, city: null, ipAddress: null };

      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout

        const locationResponse = await fetch('https://ipapi.co/json/', {
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (locationResponse.ok) {
          const locationData = await locationResponse.json();
          locationInfo = {
            country: locationData.country_name || null,
            city: locationData.city || null,
            ipAddress: locationData.ip || null
          };
        }
      } catch (error) {
        // Continue without location info
      }

      // Generate a session ID if not available
      const sessionId = localStorage.getItem(`sessionId_${chatbotId}`) ||
        `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Store session ID for future use
      localStorage.setItem(`sessionId_${chatbotId}`, sessionId);

      const submissionData = {
        chatbotId: chatbotId,
        formId: currentForm?.id || null,
        formStep: formStep,
        formData: formData,
        userId: null, // Could be enhanced later with user authentication
        sessionId: sessionId,
        ipAddress: locationInfo.ipAddress,
        userAgent: navigator.userAgent,
        country: locationInfo.country,
        city: locationInfo.city
      };

      const submissionController = new AbortController();
      const submissionTimeoutId = setTimeout(() => submissionController.abort(), 10000); // 10 second timeout

      const response = await fetch(`${getApiBaseUrl()}/api/form-submissions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
        signal: submissionController.signal
      });

      clearTimeout(submissionTimeoutId);

      if (response.ok) {
        // Form submission stored successfully
      } else {
        // Failed to store form submission - silent fail
      }
    } catch (error) {
      // Silently fail - don't pollute client console
    }
  };

  // Form handling function
  const handleFormSubmit = async (formData, formStep) => {
    // Find the form to get its prompt
    const currentForm = forms.find(form => form.step === formStep);
    const formPrompt = currentForm?.prompt || "Please provide the following information:";

    // Store form submission in database
    await storeFormSubmission(formData, formStep, currentForm);

    // Remove the form message from chat (filter out the form message for this step)
    const messagesWithoutForm = messages.filter(msg =>
      !(msg.role === 'form' && msg.formData?.step === formStep)
    );

    // Create a summary message from form data
    const formSummary = Object.entries(formData)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');

    // Add form prompt as assistant message and form data as user message
    const assistantMessage = {
      role: 'assistant',
      content: formPrompt
    };

    const userMessage = {
      role: 'user',
      content: formSummary
    };

    // Add both messages to chat (without the original form)
    const messagesWithForm = [...messagesWithoutForm, assistantMessage, userMessage];
    setMessages(messagesWithForm);

    // Clear active required form to allow input again
    setActiveRequiredForm(null);

    // Mark this form step as completed to prevent it from showing again
    setCompletedFormSteps(prev => new Set([...prev, formStep]));

    // Track step 0 form completion for menu display
    if (formStep === 0) {
      setStep0FormCompleted(true);
    }

    // Set sending state while getting AI response
    setIsSending(true);

    try {
      let aiResponse;
      let newThreadId = threadId; // Track thread ID separately

      // Check if we have custom assistant configuration or API key
      if (false) { // Server-side validation handles API key checks
        aiResponse = {
          role: 'assistant',
          content: 'API key is missing. Please configure your OpenAI API key in Advanced Settings.',
        };
      } else {
        try {
          // Use the secure server-side chat endpoint with retry logic and timeout
          const makeRetryableChatRequest = async (message, currentThreadId, retryAttempt = 0) => {
            const chatController = new AbortController();
            const chatTimeoutMs = 45000; // 45 seconds for all requests
            const chatTimeoutId = setTimeout(() => chatController.abort(), chatTimeoutMs);

            const chatResponse = await fetch(`${getApiBaseUrl()}/api/chatbot/chat`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                chatbotId: chatbotId,
                message: message,
                threadId: currentThreadId,
                retryAttempt: retryAttempt
              }),
              signal: chatController.signal
            });

            clearTimeout(chatTimeoutId);

            if (!chatResponse.ok) {
              const errorData = await chatResponse.json();
              throw new Error(errorData.error || 'Failed to process message');
            }

            const chatData = await chatResponse.json();

            // Check if the backend is requesting a retry
            if (chatData.isRetrying) {
              // Show the "taking longer than usual" message immediately
              const tempAssistantMessage = {
                role: 'assistant',
                content: sanitizeResponse(chatData.message)
              };
              const tempMessages = [...messagesWithForm, tempAssistantMessage];
              setMessages(tempMessages);

              // Wait a brief moment for user to see the message, then retry
              await new Promise(resolve => setTimeout(resolve, 1500));

              // Automatically retry with the same message
              return makeRetryableChatRequest(chatData.originalMessage, chatData.threadId, chatData.retryAttempt);
            }

            return chatData;
          };

          const chatData = await makeRetryableChatRequest(formSummary, threadId);

          aiResponse = {
            role: 'assistant',
            content: sanitizeResponse(chatData.message)
          };

          // Update thread ID if returned
          if (chatData.threadId) {
            newThreadId = chatData.threadId;
            if (chatData.threadId !== threadId) {
              setThreadId(chatData.threadId);
              localStorage.setItem(threadKey, chatData.threadId);
            }
          }

        } catch (error) {

          if (error.name === 'AbortError') {
            aiResponse = {
              role: 'assistant',
              content: 'Your request took longer than expected to process. Please try again with a shorter message or check your connection.'
            };
          } else {
            aiResponse = {
              role: 'assistant',
              content: 'Sorry, there was an error processing your form submission. Please try sending your message again.'
            };
          }
        }
      }

      // Create final messages array with AI response
      const finalMessages = [...messagesWithForm, aiResponse];

      // Handle response delay and message saving
      if (responseDelay > 0) {
        setTimeout(() => {
          setMessages(finalMessages);
          setIsSending(false);
          if (inputRef.current) {
            inputRef.current.focus();
          }

          // Save form prompt + form data, then form data + AI response
          saveMessagesToDB(assistantMessage, userMessage, newThreadId, chatbotId);
          saveMessagesToDB(userMessage, aiResponse, newThreadId, chatbotId);
        }, responseDelay * 1000);
      } else {
        setMessages(finalMessages);
        setIsSending(false);
        if (inputRef.current) {
          inputRef.current.focus();
        }

        // Save form prompt + form data, then form data + AI response
        saveMessagesToDB(assistantMessage, userMessage, newThreadId, chatbotId);
        saveMessagesToDB(userMessage, aiResponse, newThreadId, chatbotId);
      }
    } catch (error) {

      let errorContent = 'Sorry, there was an error processing your form submission. Please try again later.';

      if (error.name === 'AbortError') {
        errorContent = 'Your form submission timed out. Please try again, or contact support if the issue persists.';
      }

      const errorMessage = { role: 'assistant', content: errorContent };
      setMessages([...messagesWithForm, errorMessage]);
      setIsSending(false);
    }
  };

  // Menu display logic
  const shouldShowMenu = (showMenu, menuItems) => {
    if (!activeRequiredForm && menuItems && menuItems.length > 0) {
      const hasStep0Form = forms.some(form => form.step === 0);

      // Show menu after initial messages (intro + user hello) if no step 0 form
      const showAfterInitialMessages = messages.length <= 2 && messages.some(msg => msg.role === 'user');

      // If chat is just starting (only intro message or empty), show menu if no step 0 form
      const showAtStart = messages.length <= 1 && !hasStep0Form;

      return (
        showMenu || // Always show when triggered by "/"
        showAtStart || // Show at chat start if no step 0 form
        (!hasStep0Form && showAfterInitialMessages) || // Show after initial messages if no step 0 form
        (hasStep0Form && step0FormCompleted) // Show after step 0 form completion
      );
    }
    return false;
  };

  // Reset function for chat clearing
  const resetFormState = useCallback(() => {
    setForms([]);
    setAiResponseCount(0);
    setShownFormSteps(new Set());
    setCompletedFormSteps(new Set());
    setActiveRequiredForm(null);
    setStep0FormCompleted(false);
    setMessagesInitialized(false);

    // Reload forms after reset with a small delay to ensure clean state
    setTimeout(() => {
      if (chatbotId && isChatOpen) {
        loadForms();
      }
    }, 100);
  }, [chatbotId, isChatOpen, loadForms]);

  return {
    // State
    forms,
    activeRequiredForm,
    step0FormCompleted,
    messagesInitialized,

    // Functions
    handleFormSubmit,
    shouldShowMenu,
    resetFormState,

    // Internal state (for debugging if needed)
    aiResponseCount,
    shownFormSteps
  };
}; 